import React, { useEffect, useState } from 'react';
import api from '../api';
import { toast } from 'sonner';
import { Check, X, Gear, ShieldCheck, User, Users, Key, CaretDown, CaretUp } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const UserManagement = ({ user }) => {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [approvalDialog, setApprovalDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [permissionsDialog, setPermissionsDialog] = useState(false);
  const [permissionDefinitions, setPermissionDefinitions] = useState(null);
  const [expandedModules, setExpandedModules] = useState({});
  
  const [approvalData, setApprovalData] = useState({
    role: 'user',
    modules: ['procurement'],
    module_access: ['procurement', 'timesheets'],
    permissions: []
  });

  const [permissionsData, setPermissionsData] = useState({
    module_access: [],
    permissions: [],
    denied_permissions: []
  });

  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [pendingRes, allRes] = await Promise.all([
        api.get('/users/pending'),
        api.get('/users')
      ]);
      setPendingUsers(pendingRes.data);
      setAllUsers(allRes.data);
      
      // Load permission definitions if super admin
      if (isSuperAdmin) {
        try {
          const defsRes = await api.get('/permissions/definitions');
          setPermissionDefinitions(defsRes.data);
        } catch (e) {
          console.error('Failed to load permission definitions');
        }
      }
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveUser = async () => {
    try {
      // Get role template permissions
      const roleTemplate = permissionDefinitions?.role_templates?.[approvalData.role];
      const payload = {
        approved_by: user.id,
        role: approvalData.role,
        modules: approvalData.modules,
        module_access: roleTemplate?.modules || approvalData.module_access,
        permissions: roleTemplate?.permissions || approvalData.permissions
      };
      
      await api.put(`/users/${selectedUser.id}/approve`, payload);
      toast.success('User approved successfully');
      setApprovalDialog(false);
      setSelectedUser(null);
      loadData();
    } catch (error) {
      toast.error('Failed to approve user');
    }
  };

  const handleDenyUser = async (userId) => {
    if (!window.confirm('Are you sure you want to deny this user registration?')) return;
    try {
      await api.put(`/users/${userId}/deny`);
      toast.success('User registration denied');
      loadData();
    } catch (error) {
      toast.error('Failed to deny user');
    }
  };

  const handleUpdateUser = async () => {
    try {
      await api.put(`/users/${selectedUser.id}`, approvalData);
      toast.success('User updated successfully');
      setEditDialog(false);
      setSelectedUser(null);
      loadData();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleUpdatePermissions = async () => {
    try {
      await api.put(`/users/${selectedUser.id}/permissions`, permissionsData);
      toast.success('Permissions updated successfully');
      setPermissionsDialog(false);
      setSelectedUser(null);
      loadData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update permissions';
      toast.error(message);
      console.error('Update permissions error:', error.response?.data);
    }
  };

  const handleApplyRoleTemplate = async (role) => {
    try {
      await api.post(`/users/${selectedUser.id}/apply-role-template?role=${role}`);
      toast.success(`Applied ${role} template`);
      // Reload permissions data
      const permsRes = await api.get(`/users/${selectedUser.id}/permissions`);
      setPermissionsData({
        module_access: permsRes.data.module_access || [],
        permissions: permsRes.data.permissions || [],
        denied_permissions: permsRes.data.denied_permissions || []
      });
      loadData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to apply role template';
      toast.error(message);
      console.error('Apply role template error:', error.response?.data);
    }
  };

  const openApprovalDialog = (userItem) => {
    setSelectedUser(userItem);
    setApprovalData({
      role: 'user',
      modules: ['procurement'],
      module_access: ['procurement', 'timesheets'],
      permissions: []
    });
    setApprovalDialog(true);
  };

  const openEditDialog = (userItem) => {
    setSelectedUser(userItem);
    setApprovalData({
      role: userItem.role,
      modules: userItem.modules || ['procurement'],
      module_access: userItem.module_access || userItem.modules || [],
      permissions: userItem.permissions || []
    });
    setEditDialog(true);
  };

  const openPermissionsDialog = async (userItem) => {
    setSelectedUser(userItem);
    try {
      const permsRes = await api.get(`/users/${userItem.id}/permissions`);
      setPermissionsData({
        module_access: permsRes.data.module_access || [],
        permissions: permsRes.data.permissions || [],
        denied_permissions: permsRes.data.denied_permissions || []
      });
      // Expand first module by default
      if (permissionDefinitions?.modules?.length > 0) {
        setExpandedModules({ [permissionDefinitions.modules[0]]: true });
      }
      setPermissionsDialog(true);
    } catch (error) {
      toast.error('Failed to load user permissions');
    }
  };

  const toggleModule = (module) => {
    if (approvalData.modules.includes(module)) {
      setApprovalData({
        ...approvalData,
        modules: approvalData.modules.filter(m => m !== module)
      });
    } else {
      setApprovalData({
        ...approvalData,
        modules: [...approvalData.modules, module]
      });
    }
  };

  const toggleModuleAccess = (module) => {
    if (permissionsData.module_access.includes(module)) {
      setPermissionsData({
        ...permissionsData,
        module_access: permissionsData.module_access.filter(m => m !== module),
        // Also remove all permissions for this module
        permissions: permissionsData.permissions.filter(p => !p.startsWith(`${module}.`))
      });
    } else {
      setPermissionsData({
        ...permissionsData,
        module_access: [...permissionsData.module_access, module]
      });
    }
  };

  const togglePermission = (permission) => {
    if (permissionsData.permissions.includes(permission)) {
      setPermissionsData({
        ...permissionsData,
        permissions: permissionsData.permissions.filter(p => p !== permission)
      });
    } else {
      setPermissionsData({
        ...permissionsData,
        permissions: [...permissionsData.permissions, permission]
      });
    }
  };

  const toggleExpandedModule = (module) => {
    setExpandedModules(prev => ({
      ...prev,
      [module]: !prev[module]
    }));
  };

  const selectAllModulePermissions = (module) => {
    const modulePerms = Object.values(permissionDefinitions?.permissions?.[module] || {});
    const currentPerms = permissionsData.permissions.filter(p => !p.startsWith(`${module}.`));
    setPermissionsData({
      ...permissionsData,
      permissions: [...currentPerms, ...modulePerms]
    });
  };

  const clearAllModulePermissions = (module) => {
    setPermissionsData({
      ...permissionsData,
      permissions: permissionsData.permissions.filter(p => !p.startsWith(`${module}.`))
    });
  };

  const formatPermissionName = (perm) => {
    // Convert "procurement.manage_vendors" to "Manage Vendors"
    const action = perm.split('.')[1] || perm;
    return action.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const formatModuleName = (module) => {
    return module.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  const modules = permissionDefinitions?.modules || ['procurement', 'onboarding', 'timesheets', 'transportation', 'facilities', 'events'];

  return (
    <div data-testid="user-management-page">
      <div className="mb-8">
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#2B3210] mb-2">
          User Management
        </h1>
        <p className="text-base text-[#505631]">Manage user registrations and permissions</p>
      </div>

      <Tabs defaultValue="pending" className="w-full">
        <TabsList className="mb-6 bg-[#FBF8EF] border border-[#E5E2D9]">
          <TabsTrigger value="pending" className="data-[state=active]:bg-white" data-testid="tab-pending-users">
            <User size={16} className="mr-2" />
            Pending Approvals ({pendingUsers.length})
          </TabsTrigger>
          <TabsTrigger value="all" className="data-[state=active]:bg-white" data-testid="tab-all-users">
            <Users size={16} className="mr-2" />
            All Users ({allUsers.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending">
          <div className="bg-white rounded-xl border border-[#E5E2D9] overflow-hidden">
            <div className="p-6 border-b border-[#E5E2D9]">
              <h2 className="text-xl font-medium text-[#2B3210]">Pending Registrations</h2>
            </div>
            {pendingUsers.length === 0 ? (
              <div className="p-8 text-center text-[#505631]">
                No pending registrations
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#FBF8EF] border-b border-[#E5E2D9]">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase tracking-wider">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase tracking-wider">Department</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase tracking-wider">Registered</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-[#505631] uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E5E2D9]">
                    {pendingUsers.map((pendingUser) => (
                      <tr key={pendingUser.id} className="hover:bg-[#FBF8EF]">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-[#2B3210]">{pendingUser.full_name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-[#505631]">{pendingUser.email}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-[#505631]">{pendingUser.department}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-[#505631]">
                          {new Date(pendingUser.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                          <button
                            onClick={() => openApprovalDialog(pendingUser)}
                            className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg mr-2"
                            title="Approve"
                            data-testid={`approve-user-${pendingUser.id}`}
                          >
                            <Check size={20} weight="bold" />
                          </button>
                          <button
                            onClick={() => handleDenyUser(pendingUser.id)}
                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                            title="Deny"
                          >
                            <X size={20} weight="bold" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="all">
          <div className="bg-white rounded-xl border border-[#E5E2D9] overflow-hidden">
            <div className="p-6 border-b border-[#E5E2D9]">
              <h2 className="text-xl font-medium text-[#2B3210]">All Users</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FBF8EF] border-b border-[#E5E2D9]">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase tracking-wider">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase tracking-wider">Department</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase tracking-wider">Role</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase tracking-wider">Modules</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-[#505631] uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E2D9]">
                  {allUsers.map((u) => (
                    <tr key={u.id} className="hover:bg-[#FBF8EF]">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-[#2B3210] font-medium">{u.full_name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-[#505631]">{u.email}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-[#505631]">{u.department}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          u.role === 'super_admin' ? 'bg-purple-100 text-purple-700' :
                          u.role === 'admin' ? 'bg-blue-100 text-blue-700' :
                          u.role === 'manager' ? 'bg-amber-100 text-amber-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {u.role}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          u.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                          u.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {u.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1 max-w-xs">
                          {(u.module_access || u.modules || []).slice(0, 3).map(m => (
                            <span key={m} className="px-2 py-0.5 bg-[#FBF8EF] text-[#505631] rounded text-xs">
                              {formatModuleName(m)}
                            </span>
                          ))}
                          {(u.module_access || u.modules || []).length > 3 && (
                            <span className="px-2 py-0.5 bg-[#E5E2D9] text-[#505631] rounded text-xs">
                              +{(u.module_access || u.modules || []).length - 3}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                        {u.status === 'approved' && u.id !== user.id && (
                          <>
                            <button
                              onClick={() => openEditDialog(u)}
                              className="p-2 text-[#B2560D] hover:bg-orange-50 rounded-lg"
                              title="Edit User"
                            >
                              <Gear size={18} />
                            </button>
                            {isSuperAdmin && (
                              <button
                                onClick={() => openPermissionsDialog(u)}
                                className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg ml-1"
                                title="Manage Permissions"
                                data-testid={`permissions-${u.id}`}
                              >
                                <Key size={18} />
                              </button>
                            )}
                          </>
                        )}
                        {u.id === user.id && (
                          <span className="text-xs text-[#505631]">You</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Approval Dialog */}
      <Dialog open={approvalDialog} onOpenChange={setApprovalDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Approve User: {selectedUser?.full_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#2B3210] mb-2">Assign Role</label>
              <select
                value={approvalData.role}
                onChange={(e) => setApprovalData({ ...approvalData, role: e.target.value })}
                className="w-full px-4 py-2 border border-[#E5E2D9] rounded-lg"
              >
                <option value="user">User</option>
                <option value="manager">Manager</option>
                <option value="admin">Admin</option>
                {isSuperAdmin && <option value="super_admin">Super Admin</option>}
              </select>
              <p className="text-xs text-[#505631] mt-1">
                Role determines default permissions. You can customize later.
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-[#2B3210] mb-2">Module Access</label>
              <div className="grid grid-cols-2 gap-2">
                {modules.filter(m => m !== 'user_management').map(module => (
                  <label
                    key={module}
                    className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
                      approvalData.modules.includes(module)
                        ? 'bg-[#B2560D] text-white border-[#B2560D]'
                        : 'bg-white border-[#E5E2D9] hover:border-[#B2560D]'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={approvalData.modules.includes(module)}
                      onChange={() => toggleModule(module)}
                      className="sr-only"
                    />
                    <span className="text-sm capitalize">{formatModuleName(module)}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                onClick={() => setApprovalDialog(false)}
                className="flex-1 px-4 py-2 border border-[#E5E2D9] rounded-lg text-[#505631] hover:bg-[#FBF8EF]"
              >
                Cancel
              </button>
              <button
                onClick={handleApproveUser}
                className="flex-1 btn-primary"
              >
                Approve User
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit User: {selectedUser?.full_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#2B3210] mb-2">Role</label>
              <select
                value={approvalData.role}
                onChange={(e) => setApprovalData({ ...approvalData, role: e.target.value })}
                className="w-full px-4 py-2 border border-[#E5E2D9] rounded-lg"
              >
                <option value="user">User</option>
                <option value="manager">Manager</option>
                <option value="admin">Admin</option>
                {isSuperAdmin && <option value="super_admin">Super Admin</option>}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-[#2B3210] mb-2">Module Access</label>
              <div className="grid grid-cols-2 gap-2">
                {modules.filter(m => m !== 'user_management').map(module => (
                  <label
                    key={module}
                    className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
                      approvalData.modules.includes(module)
                        ? 'bg-[#B2560D] text-white border-[#B2560D]'
                        : 'bg-white border-[#E5E2D9] hover:border-[#B2560D]'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={approvalData.modules.includes(module)}
                      onChange={() => toggleModule(module)}
                      className="sr-only"
                    />
                    <span className="text-sm capitalize">{formatModuleName(module)}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                onClick={() => setEditDialog(false)}
                className="flex-1 px-4 py-2 border border-[#E5E2D9] rounded-lg text-[#505631] hover:bg-[#FBF8EF]"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateUser}
                className="flex-1 btn-primary"
              >
                Save Changes
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Granular Permissions Dialog */}
      <Dialog open={permissionsDialog} onOpenChange={setPermissionsDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck size={24} className="text-purple-600" />
              Manage Permissions: {selectedUser?.full_name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4 py-4">
            {/* Role Template Quick Apply */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <p className="text-sm font-medium text-purple-800 mb-2">Quick Apply Role Template</p>
              <div className="flex flex-wrap gap-2">
                {['user', 'manager', 'admin'].map(role => (
                  <button
                    key={role}
                    onClick={() => handleApplyRoleTemplate(role)}
                    data-testid={`apply-template-${role}`}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      selectedUser?.role === role
                        ? 'bg-purple-600 text-white'
                        : 'bg-white border border-purple-300 text-purple-700 hover:bg-purple-100'
                    }`}
                  >
                    {role.charAt(0).toUpperCase() + role.slice(1)}
                  </button>
                ))}
              </div>
              <p className="text-xs text-purple-600 mt-2">
                Applying a template will reset permissions to that role's defaults
              </p>
            </div>

            {/* Module Access */}
            <div>
              <h3 className="text-sm font-semibold text-[#2B3210] mb-3 flex items-center gap-2">
                <Users size={18} />
                Module Access
              </h3>
              <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                {modules.map(module => (
                  <label
                    key={module}
                    className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${
                      permissionsData.module_access.includes(module)
                        ? 'bg-[#505631] text-white border-[#505631]'
                        : 'bg-white border-[#E5E2D9] hover:border-[#505631] text-[#2B3210]'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={permissionsData.module_access.includes(module)}
                      onChange={() => toggleModuleAccess(module)}
                      className="sr-only"
                    />
                    <span className="text-xs font-medium">{formatModuleName(module)}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Granular Permissions by Module */}
            <div>
              <h3 className="text-sm font-semibold text-[#2B3210] mb-3 flex items-center gap-2">
                <Key size={18} />
                Granular Permissions
              </h3>
              <div className="space-y-2">
                {modules.filter(m => permissionsData.module_access.includes(m)).map(module => {
                  const modulePerms = permissionDefinitions?.permissions?.[module] || {};
                  const isExpanded = expandedModules[module];
                  const permCount = Object.values(modulePerms).filter(p => permissionsData.permissions.includes(p)).length;
                  const totalPerms = Object.keys(modulePerms).length;
                  
                  return (
                    <div key={module} className="border border-[#E5E2D9] rounded-lg overflow-hidden">
                      <button
                        onClick={() => toggleExpandedModule(module)}
                        className="w-full flex items-center justify-between p-3 bg-[#FBF8EF] hover:bg-[#f5f0e3] transition-colors"
                      >
                        <span className="font-medium text-[#2B3210]">{formatModuleName(module)}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs px-2 py-0.5 bg-white rounded-full text-[#505631]">
                            {permCount}/{totalPerms}
                          </span>
                          {isExpanded ? <CaretUp size={16} /> : <CaretDown size={16} />}
                        </div>
                      </button>
                      
                      {isExpanded && (
                        <div className="p-3 bg-white">
                          <div className="flex gap-2 mb-3">
                            <button
                              onClick={() => selectAllModulePermissions(module)}
                              className="text-xs text-emerald-600 hover:underline"
                            >
                              Select All
                            </button>
                            <span className="text-[#E5E2D9]">|</span>
                            <button
                              onClick={() => clearAllModulePermissions(module)}
                              className="text-xs text-red-600 hover:underline"
                            >
                              Clear All
                            </button>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                            {Object.entries(modulePerms).map(([key, permission]) => (
                              <label
                                key={permission}
                                className={`flex items-center gap-2 p-2 rounded border cursor-pointer text-sm transition-colors ${
                                  permissionsData.permissions.includes(permission)
                                    ? 'bg-emerald-50 border-emerald-300 text-emerald-800'
                                    : 'bg-white border-[#E5E2D9] hover:border-emerald-300 text-[#505631]'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={permissionsData.permissions.includes(permission)}
                                  onChange={() => togglePermission(permission)}
                                  className="sr-only"
                                />
                                <Check 
                                  size={14} 
                                  className={permissionsData.permissions.includes(permission) ? 'text-emerald-600' : 'text-transparent'} 
                                />
                                <span className="text-xs">{formatPermissionName(permission)}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                
                {permissionsData.module_access.length === 0 && (
                  <p className="text-sm text-[#505631] text-center py-4">
                    Select modules above to configure their permissions
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t border-[#E5E2D9]">
            <button
              onClick={() => setPermissionsDialog(false)}
              className="flex-1 px-4 py-2 border border-[#E5E2D9] rounded-lg text-[#505631] hover:bg-[#FBF8EF]"
            >
              Cancel
            </button>
            <button
              onClick={handleUpdatePermissions}
              className="flex-1 btn-primary flex items-center justify-center gap-2"
            >
              <ShieldCheck size={18} />
              Save Permissions
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;
